import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@mui/material'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CloseIcon from '@mui/icons-material/Close'
import Image from 'next/image'
import React from 'react'

import PassportGuideImage from '@/src/assets/png/passport.png'
import PassportIcon from '@/src/assets/svgs/PassportIcon'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'

interface InstructionItem {
    key: string
    ok: boolean
    label: string
}

interface PassportScanViewProps {
    isMobile: boolean
    isBusy: boolean
    busyColor: string
    isInfoDrawerOpen: boolean
    onInfoClick: () => void
    onCloseInfoDrawer: () => void
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
    uploadRef: React.MutableRefObject<HTMLInputElement | null>
    cameraRef: React.MutableRefObject<HTMLInputElement | null>
    titleLabel: string
    subtitleLabel: string
    uploadLabel: string
    cameraLabel: string
    tooltipTitle: string
    instructionItems: InstructionItem[]
    sizeError: string | null
    showNote: boolean
    reviewNote: string
}

const PassportHelpContent = ({
    tooltipTitle,
    instructionItems,
    variant = 'tooltip'
}: {
    tooltipTitle: string
    instructionItems: InstructionItem[]
    variant?: 'tooltip' | 'drawer'
}) => (
    <Box sx={{ p: variant === 'drawer' ? 0 : 0.5, maxWidth: variant === 'drawer' ? 'none' : 260 }}>
        <Box sx={{ width: '100%' }}>
            <Image
                src={PassportGuideImage}
                alt="Passport guide sample"
                style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '6px',
                    display: 'block'
                }}
            />
        </Box>
        <Typography
            sx={{
                fontSize: variant === 'drawer' ? 16 : 11,
                fontWeight: 600,
                color: variant === 'drawer' ? 'textColors.primary' : 'common.white',
                mt: variant === 'drawer' ? 2 : 1.25,
                mb: variant === 'drawer' ? 1.5 : 0.75,
                letterSpacing: variant === 'drawer' ? 0 : 0.2,
                lineHeight: variant === 'drawer' ? 1.4 : 1.2
            }}
        >
            {tooltipTitle}
        </Typography>
        {instructionItems.map((item) => (
            <Box
                key={item.key}
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: variant === 'drawer' ? 1 : 0.75,
                    mb: variant === 'drawer' ? 1 : 0.5
                }}
            >
                {item.ok
                    ? (
                        <CheckCircleOutlineIcon
                            sx={{
                                fontSize: variant === 'drawer' ? 16 : 13,
                                color: 'success.main',
                                mt: '1px',
                                flexShrink: 0
                            }}
                        />
                    )
                    : (
                        <CancelOutlinedIcon
                            sx={{
                                fontSize: variant === 'drawer' ? 16 : 13,
                                color: 'error.main',
                                mt: '1px',
                                flexShrink: 0
                            }}
                        />
                    )
                }
                <Typography
                    sx={{
                        fontSize: variant === 'drawer' ? 14 : 11,
                        fontWeight: variant === 'drawer' ? 500 : 400,
                        color: variant === 'drawer' ? 'textColors.secondary' : 'grey.300',
                        lineHeight: variant === 'drawer' ? 1.6 : 1.4
                    }}
                >
                    {item.label}
                </Typography>
            </Box>
        ))}
    </Box>
)

const PassportScan = ({
    isMobile,
    isBusy,
    busyColor,
    isInfoDrawerOpen,
    onInfoClick,
    onCloseInfoDrawer,
    onFileChange,
    uploadRef,
    cameraRef,
    titleLabel,
    subtitleLabel,
    uploadLabel,
    cameraLabel,
    tooltipTitle,
    instructionItems,
    sizeError,
    showNote,
    reviewNote
}: PassportScanViewProps) => {
    const hasBanner = Boolean(sizeError || showNote)

    const btnSx = {
        fontSize: 14,
        fontWeight: 600,
        textTransform: 'none',
        borderColor: 'grey.300',
        color: 'textColors.primary',
        borderRadius: '8px',
        px: 1.5,
        py: 0.75,
        minWidth: 0,
        '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
    } as const

    return (
        <Box sx={{ mb: 3, mt: 1 }}>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'borders.default',
                    borderRadius: hasBanner ? '18px 18px 0 0' : '18px',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.5, sm: 2 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bgcolor: 'success.main',
                        color: 'common.white',
                        borderBottomRightRadius: '12px',
                        width: 44,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 1
                    }}
                >
                    NEW
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, pr: 1 }}>
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '16px',
                                bgcolor: 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            {isBusy ? (
                                <CircularProgress size={16} sx={{ color: busyColor }} />
                            ) : (
                                <PassportIcon sx={{ width: 20, height: 28 }} />
                            )}
                        </Box>

                        <Box sx={{ minWidth: 0, pt: 0.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: { xs: 700, md: 600 }, color: 'textColors.primary', lineHeight: 1.4 }}>
                                {titleLabel}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    lineHeight: 1.4,
                                    color: isBusy ? busyColor : 'textColors.secondary'
                                }}
                            >
                                {subtitleLabel}
                            </Typography>
                        </Box>
                    </Box>

                    <Tooltip
                        title={<PassportHelpContent tooltipTitle={tooltipTitle} instructionItems={instructionItems} />}
                        placement="top-end"
                        arrow
                        disableHoverListener={isMobile}
                        disableFocusListener={isMobile}
                        disableTouchListener={isMobile}
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: 'grey.900',
                                    borderRadius: '10px',
                                    p: 1.25,
                                    maxWidth: 280,
                                    boxShadow: 6,
                                },
                            },
                            arrow: { sx: { color: 'grey.900' } },
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={onInfoClick}
                            sx={{
                                color: 'textColors.secondary',
                                p: 0.5,
                                mt: 0.3,
                                '&:hover': { color: 'textColors.primary', bgcolor: 'transparent' },
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                        <Button
                            component="label"
                            size="small"
                            variant="outlined"
                            startIcon={<CameraAltOutlinedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                ...btnSx,
                                flex: 1,
                                height: 40,
                                borderRadius: '8px',
                                py: '4px',
                                px: '16px',
                                gap: '12px'
                            }}
                            disabled={isBusy}
                        >
                            {cameraLabel}
                            <input
                                ref={cameraRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                hidden
                                onChange={onFileChange}
                            />
                        </Button>

                        <Button
                            component="label"
                            size="small"
                            variant="outlined"
                            startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                ...btnSx,
                                flex: 1,
                                height: 40,
                                borderRadius: '8px',
                                py: '4px',
                                px: '16px',
                                gap: '12px'
                            }}
                            disabled={isBusy}
                        >
                            {uploadLabel}
                            <input
                                ref={uploadRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                hidden
                                onChange={onFileChange}
                            />
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ position: 'absolute', right: 50, top: '50%', transform: 'translateY(-50%)' }}>
                        <Button
                            component="label"
                            size="small"
                            variant="outlined"
                            startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                ...btnSx,
                                height: 40,
                                borderRadius: '8px',
                                py: '4px',
                                px: '20px',
                                gap: '12px'
                            }}
                            disabled={isBusy}
                        >
                            {uploadLabel}
                            <input
                                ref={uploadRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                hidden
                                onChange={onFileChange}
                            />
                        </Button>
                    </Box>
                )}
            </Box>

            {sizeError && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: 'error.50',
                        border: '1px solid',
                        borderColor: 'borders.default',
                        borderTop: '1px solid',
                        borderTopColor: 'error.200',
                        borderRadius: showNote ? '0' : '0 0 10px 10px',
                    }}
                >
                    <CancelOutlinedIcon sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, color: 'error.dark', lineHeight: 1.5 }}>
                        {sizeError}
                    </Typography>
                </Box>
            )}

            {showNote && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: 'warning.50',
                        border: '1px solid',
                        borderColor: 'borders.default',
                        borderTop: '1px solid',
                        borderTopColor: 'warning.200',
                        borderRadius: '0 0 10px 10px',
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, color: 'warning.dark', lineHeight: 1.5 }}>
                        {reviewNote}
                    </Typography>
                </Box>
            )}

            <SwipeableDrawerWithBackNavigation
                open={isInfoDrawerOpen}
                onClose={onCloseInfoDrawer}
                sx={{ height: 'auto', maxHeight: '85dvh' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pb: 1, borderBottom: '1px solid', borderBottomColor: 'borders.default' }}>
                    <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }}>
                        <IconButton
                            size="small"
                            onClick={onCloseInfoDrawer}
                            sx={{ color: 'textColors.primary', p: 0.5 }}
                        >
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'textColors.primary' }}>
                            {tooltipTitle}
                        </Typography>
                    </Box>
                    <Box sx={{ width: 40 }} />
                </Box>

                <Box sx={{ px: 2, py: 2, overflowY: 'auto' }}>
                    <PassportHelpContent tooltipTitle={tooltipTitle} instructionItems={instructionItems} variant="drawer" />
                </Box>
            </SwipeableDrawerWithBackNavigation>
        </Box>
    )
}

export default PassportScan
